import type { AITTheme } from '@this/shared/ui/theme';
import type { CSSProperties } from 'react';

export type AdminFilterSegmentPosition = 'first' | 'middle' | 'last' | 'only';

export function getAdminFilterBarStyles(theme: AITTheme) {
  const brandPrimary = theme.tokens.colors.brand.primary;
  const segmentBorderColor = '#D3D7DB';
  const segmentSelectedBackground = theme.tokens.colors.brand.background;

  const getSegmentFaceStyle = (position: AdminFilterSegmentPosition, selected: boolean): CSSProperties => {
    const hasRightDivider = position === 'first' || position === 'middle';

    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '0 16px',
      boxSizing: 'border-box',
      fontSize: '13px',
      fontWeight: selected ? 700 : 400,
      lineHeight: 1.5,
      fontFamily: 'Noto Sans JP,sans-serif',
      whiteSpace: 'nowrap',
      border: 'none',
      borderRight: hasRightDivider ? `1px solid ${segmentBorderColor}` : 'none',
      borderRadius: 0,
      margin: 0,
      backgroundColor: selected ? segmentSelectedBackground : theme.tokens.colors.common.white,
      color: selected ? brandPrimary : theme.tokens.colors.text.primary,
      cursor: 'pointer',
      userSelect: 'none'
    };
  };

  return {
    filterBar: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      alignItems: 'center' as const,
      columnGap: '16px',
      rowGap: '12px',
      width: '100%',
      marginBottom: '20px',
      boxSizing: 'border-box' as const
    } as const,
    filterBarStart: {
      display: 'flex',
      alignItems: 'flex-end' as const,
      flexWrap: 'wrap' as const,
      gap: '16px',
      justifySelf: 'start' as const,
      minWidth: 0
    } as const,
    filterBarEnd: {
      display: 'flex',
      alignItems: 'flex-end' as const,
      flexWrap: 'wrap' as const,
      gap: '16px',
      justifySelf: 'end' as const
    } as const,
    filterGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
      alignItems: 'flex-start' as const
    } as const,
    filterGroupLabel: {
      fontSize: '12px',
      fontWeight: 400,
      color: '#959595',
      lineHeight: 1,
      flexShrink: 0
    } as const,
    filterGroupControl: {
      display: 'flex',
      alignItems: 'center' as const,
      flexWrap: 'wrap' as const,
      gap: '8px'
    } as const,
    historySelectFormControl: {
      width: '320px',
      maxWidth: '100%',
      minWidth: '200px',
      margin: 0
    } as const,
    filterBarSelectInput: {
      height: '32px',
      padding: '4px 8px',
      fontSize: '14px',
      lineHeight: '20px',
      boxSizing: 'border-box' as const
    } as const,
    searchInput: {
      width: '300px',
      maxWidth: '100%',
      minWidth: '200px'
    } as const,
    segmentedControl: {
      display: 'inline-flex',
      flexWrap: 'nowrap' as const,
      verticalAlign: 'top' as const,
      alignItems: 'stretch' as const,
      height: '32px',
      boxSizing: 'border-box' as const,
      border: `1px solid ${segmentBorderColor}`,
      borderRadius: '4px',
      overflow: 'hidden' as const,
      backgroundColor: theme.tokens.colors.common.white
    } as const,
    segmentedLabel: {
      display: 'inline-flex',
      flex: '1 1 auto',
      margin: 0,
      cursor: 'pointer' as const
    } as const,
    segmentedInput: {
      position: 'absolute' as const,
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden' as const,
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap' as const,
      border: 0
    } as const,
    getSegmentFaceStyle
  };
}
